import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';

import { GridReadyEvent, IServerSideDatasource, RowSelectedEvent } from '@ag-grid-community/core';
import { ActivatedRoute } from '@angular/router';
import { AgFns, GridOptionsExt, ISortModel } from 'app/shared/ag-fns';
import { NavFns } from 'app/shared/nav-fns';
import { takeUntil } from 'rxjs/operators';
import { DomainBaseComponent } from '../shared/domain-base.component';
import { DomainService } from '../shared/domain.service';
import { InvoiceHeader, JoHeader, Manifest, ManifestGroup, ManifestGroupInvoice } from './../model/entities/entity-model';
import { EventScheduleDialogComponent } from './event-schedule-dialog.component';

import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { InvoiceDialogComponent } from 'app/shared/invoice-dialog.component';
import { QbHealthService } from 'app/shared/qb-health.service';
import { UtilFns } from 'app/shared/util-fns';

@Component({
  selector: 'app-manifests',
  templateUrl: './manifest.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ManifestComponent extends DomainBaseComponent implements OnInit {
  @ViewChild('invoiceScheduleCell') invoiceScheduleCell!: TemplateRef<any>;
  @ViewChild('sendManifestCell') sendManifestCell!: TemplateRef<any>;
  @ViewChild('viewManifestCell') viewManifestCell!: TemplateRef<any>;

  manifestGroupGridOptions!: GridOptionsExt;
  manifestGroupDatasource!: IServerSideDatasource;
  currentManifestGroup!: ManifestGroup | null;

  submittedGroupGridOptions!: GridOptionsExt;

  shouldReselect!: boolean;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    public qbHealth: QbHealthService,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.manifestGroupGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onManifestGroupGridReady,
        onRowSelected: this.onRowSelected,
        onModelUpdated: this.onModelUpdated,
        rowModelType: 'serverSide',
        onPaginationChanged: (params) => {
          if (params.newPage) {
            this.currentManifestGroup = null;
            this.updateLocation();
          }
        },
      },
      { detailProperty: 'manifestGroupInvoices' },
    );
    AgFns.captureGridRouteParams(this.manifestGroupGridOptions, this.route, 'id');
    this.updateManifestGroupMasterDetail(this.manifestGroupGridOptions);

    this.submittedGroupGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onSubmittedGroupGridReady,
        rowModelType: 'serverSide',
      },
      { detailProperty: 'manifestGroupInvoices' },
    );
    this.updateManifestGroupMasterDetail(this.submittedGroupGridOptions);

    this.isPageReady = true;
    await this.qbHealth.refresh();
  }

  override async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Manifest);
    this.uow.clearEntities(ManifestGroup);
    this.uow.clearEntities(ManifestGroupInvoice);
    this.uow.clearEntities(InvoiceHeader);
    this.uow.clearEntities(JoHeader);
    return true;
  }

  onManifestGroupGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      {
        headerName: 'Manifest Group Id',
        field: 'id',
        filter: 'agNumberColumnFilter',
        type: 'leftAligned',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Manifest',
        field: 'manifest.manifestName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Account',
        valueGetter: (params: any) => {
          const mg = params.data as ManifestGroup;
          const jo = mg?.manifest?.joHeaders?.[0];
          return jo?.account?.accountName ?? '';
        },
        sortable: false,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Invoices',
        valueGetter: (params: any) => {
          const mg = params.data as ManifestGroup;
          return mg?.manifestGroupInvoices?.length ?? 0;
        },
        ...AgFns.fixWidth(100),
      },
      {
        ...AgFns.createCellButtonProps('Invoice Schedule', this.invoiceScheduleCell, 'invoiceScheduleCell'),
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Next Process Date',
        valueGetter: (params: any) => {
          const mg = params.data as ManifestGroup;
          return mg?.manifest?.nextProcessDate ?? null;
        },
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth(200),
      },
      {
        ...AgFns.createButtonProps('Preview', this.previewManifestGroup.bind(this), {
          label: 'Preview',
          canDisplay: () => this.qbHealth.isQbHealthy && !this.qbHealth.isCheckingQb,
        }),
      },
      {
        ...AgFns.createCellButtonProps('Send Manifest', this.sendManifestCell, 'sendManifest'),
      },
    ];

    AgFns.initGrid(event, colDefs);
    this.updateDatasource();
    AgFns.applyGridRouteParams(this.manifestGroupGridOptions);
  };

  onSubmittedGroupGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      {
        headerName: 'Group Id',
        field: 'id',
        filter: 'agNumberColumnFilter',
        type: 'leftAligned',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Account',
        colId: 'account',
        valueGetter: (params: any) => {
          const mg = params.data as ManifestGroup;
          const jo = mg?.manifest?.joHeaders?.[0];
          return jo?.account?.accountName ?? '';
        },
        filter: 'agTextColumnFilter',
        sortable: false,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Manifest',
        field: 'manifest.manifestName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Invoices',
        valueGetter: (params: any) => {
          const mg = params.data as ManifestGroup;
          return mg?.manifestGroupInvoices?.length ?? 0;
        },
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Sent Date',
        field: 'sentDate',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth(130),
      },
      {
        ...AgFns.createCellButtonProps('View Manifest', this.viewManifestCell),
      },
    ];
    const sortModel = [{ colId: 'id', sort: 'desc' }] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateSubmittedGroupDatasource();
  };

  updateManifestGroupMasterDetail(parentGridOptions: GridOptionsExt) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      {
        headerName: 'Invoice',
        field: 'invoiceHeaderId',
        sort: 'desc' as const,
        ...NavFns.createCellClickedDialogProps((invoiceId: string) =>
          InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, { invoiceId }),
        ),
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps('invoiceHeader.joHeaderId', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Account User',
        valueGetter: (params: any) => {
          const mgi = params.data as ManifestGroupInvoice;
          return mgi?.invoiceHeader?.joHeader?.getFormalEmployeeName() ?? '';
        },
        ...AgFns.flexWidth(160),
      },
      {
        headerName: 'Amount',
        valueGetter: (params: any) => {
          const mgi = params.data as ManifestGroupInvoice;
          return mgi?.invoiceHeader?.roundedTotalAmt() ?? 0;
        },
        type: 'numericColumn',
        valueFormatter: (params: any) => (params.value != null ? UtilFns.fmtCurrency(params.value) : ''),
        ...AgFns.fixWidth(160),
      },
      { headerName: 'Invoice Date', field: 'invoiceHeader.invoiceDate', ...AgFns.fixWidth(160) },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const mg = params.data as ManifestGroup;
        const q = this.uow
          .createQuery(ManifestGroupInvoice, 'ManifestGroupInvoices')
          .where({ manifestGroupId: mg.id })
          .expand(['invoiceHeader.joHeader', 'invoiceHeader.invoiceDetails.joDetail']);
        q.execute().then((mgis) => params.successCallback(mgis));
      },
    };
  }

  async updateDatasource() {
    const gridApi = this.manifestGroupGridOptions?.apiX;
    if (gridApi == null) return;

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createUnsentManifestGroupQuery());
    this.manifestGroupDatasource = ds;

    gridApi.setGridOption('serverSideDatasource', ds);
  }

  updateSubmittedGroupDatasource() {
    const gridApi = this.submittedGroupGridOptions?.apiX;
    if (gridApi == null) return;

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createManifestGroupQuery(), undefined, {
      filterClauseFns: {
        account: (value) => ({ 'manifest.joHeaders': { any: { 'account.accountName': value } } }),
      },
    });
    gridApi.setGridOption('serverSideDatasource', ds);
  }

  updateLocation(key: any = null) {
    const urlTree = this.router.createUrlTree(['/manifests'], {});
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.manifestGroupGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  async onRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) {
      return;
    }

    const mg = e.data as ManifestGroup;
    if (!mg) {
      return;
    }
    this.currentManifestGroup = mg;
    this.updateLocation(mg.id);
  }

  async onModelUpdated(params) {
    const gridApi = this.manifestGroupGridOptions.apiX;

    await this.ensureNextProcessDates();

    const nodes = gridApi?.getSelectedNodes();
    if (nodes == null || nodes.length == 0) {
      return null;
    }
    const rse = <RowSelectedEvent>{ node: nodes[0], data: nodes[0].data };
    this.onRowSelected(rse);
  }

  async ensureNextProcessDates() {
    const gridApi = this.manifestGroupGridOptions.apiX;
    if (gridApi == null) return;

    // Missing, or already behind us. A stored date stays where the last send left it, so a manifest
    // that skipped runs for want of invoices still reads its first missed run until recomputed here.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const manifests: Manifest[] = [];
    gridApi.forEachNode((node) => {
      const mg = node.data as ManifestGroup;
      const m = mg?.manifest;
      const isStale = m?.nextProcessDate == null || m.nextProcessDate < today;
      if (m?.rRule && isStale && !manifests.includes(m)) {
        manifests.push(m);
      }
    });

    if (manifests.length === 0) return;

    let saved = false;
    for (const manifest of manifests) {
      const nextProcessDate = await this.dbQueryService.getManifestNextProcessDate(manifest);
      if (nextProcessDate != null) {
        manifest.nextProcessDate = nextProcessDate;
        saved = true;
      }
    }

    if (saved) {
      await this.uow.commit();
      gridApi.refreshCells({ force: true });
    }
  }

  async viewEventSchedule(mg: ManifestGroup) {
    const rrule = mg?.manifest?.rRule;
    if (!rrule) return;

    await DialogBaseComponent.open(EventScheduleDialogComponent, this, {
      rruleString: rrule,
    });
  }

  previewManifestGroup(mg: ManifestGroup) {
    if (mg == null) return;
    this.router.navigate(['/printable-manifest-group', mg.id], { queryParams: { preview: true } });
  }

  canSendManifest(mg: ManifestGroup) {
    return mg != null && this.qbHealth.isQbHealthy && !this.qbHealth.isCheckingQb;
  }

  async sendManifest(mg: ManifestGroup) {
    if (mg == null) return;
    this.router.navigate(['/printable-manifest-group', mg.id]);
  }

  viewManifest(mg: ManifestGroup) {
    if (mg != null) {
      this.router.navigate(['/printable-manifest-group', mg.id]);
    }
  }
}
